import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { panelFeedbackGet, panelFeedbackSubmit } from '@/lib/otcApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, Star } from 'lucide-react';

function FullScreen({ children }) {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-secondary/30 p-4">
      {children}
    </div>
  );
}

function Notice({ icon: Icon, colour, title, body }) {
  const tone = {
    rose: 'text-rose-600',
    emerald: 'text-emerald-600',
  }[colour] || 'text-muted-foreground';
  return (
    <Card className="card-elevated max-w-lg w-full">
      <CardContent className="p-7 text-center">
        <Icon className={`mx-auto h-10 w-10 ${tone}`} />
        <h1 className="mt-3 text-xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

function StarRating({ value, onChange, name }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid={`stars-${name}`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="p-0.5"
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            data-testid={`star-${name}-${n}`}
          >
            <Star
              className={`h-6 w-6 ${active ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`}
            />
          </button>
        );
      })}
      <span className="ml-2 text-xs text-muted-foreground tabular-nums">
        {value ? `${value}/5` : ''}
      </span>
    </div>
  );
}

export default function PanelFeedback() {
  const { token } = useParams();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['panel-feedback', token],
    queryFn: () => panelFeedbackGet(token),
    retry: false,
  });

  const [scores, setScores] = useState({});
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);

  const categories = useMemo(() => data?.categories || [], [data]);
  const allRated = categories.length > 0 && categories.every((c) => scores[c] > 0);

  const submitMut = useMutation({
    mutationFn: () => panelFeedbackSubmit(token, {
      scores: categories.map((c) => ({ label: c, score: scores[c] })),
      note: note.trim() || null,
    }),
    onSuccess: () => setDone(true),
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 410) {
        setDone(true);
        return;
      }
      // eslint-disable-next-line no-alert
      alert(typeof detail === 'string' ? detail : 'Could not submit feedback.');
    },
  });

  if (isLoading) {
    return <FullScreen><p className="text-sm text-muted-foreground">Loading feedback form…</p></FullScreen>;
  }
  if (!data || data.state === 'invalid') {
    return (
      <FullScreen>
        <Notice
          icon={XCircle}
          colour="rose"
          title="This link is not valid"
          body="The feedback link is incorrect, expired, or has been replaced."
        />
      </FullScreen>
    );
  }
  if (done || data.state === 'submitted') {
    return (
      <FullScreen>
        <Notice
          icon={CheckCircle2}
          colour="emerald"
          title="Feedback submitted"
          body="Thank you for sharing your feedback. This link can no longer be used."
        />
      </FullScreen>
    );
  }

  return (
    <FullScreen>
      <Card className="card-elevated max-w-2xl w-full">
        <CardContent className="p-7 space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              Kadel Labs · {data.round_label}
            </p>
            <h1 className="mt-1 text-2xl font-semibold">Interview feedback</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Candidate: <span className="font-medium text-foreground">{data.candidate_name}</span>
              {data.role_label ? ` · ${data.role_label}` : ''}
            </p>
          </div>

          {(data.questions || []).length > 0 && (
            <div className="rounded-xl border bg-secondary/40 p-4">
              <p className="text-sm font-semibold">Questions covered</p>
              <ol className="mt-2 ml-4 list-decimal space-y-1 text-sm text-muted-foreground">
                {data.questions.map((q, i) => <li key={i}>{q}</li>)}
              </ol>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-sm font-semibold">Ratings (1–5)</p>
            {categories.map((cat) => (
              <div
                key={cat}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3"
              >
                <span className="text-sm">{cat}</span>
                <StarRating
                  name={cat}
                  value={scores[cat] || 0}
                  onChange={(n) => setScores((prev) => ({ ...prev, [cat]: n }))}
                />
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-semibold">Feedback notes (optional)</label>
            <Textarea
              rows={5}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Share any additional observations about the candidate…"
              className="mt-1"
              data-testid="feedback-note"
            />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {allRated ? 'All categories rated.' : 'Please rate every category to submit.'}
            </p>
            <Button
              onClick={() => submitMut.mutate()}
              disabled={!allRated || submitMut.isPending}
              data-testid="feedback-submit"
            >
              {submitMut.isPending ? 'Submitting…' : 'Submit feedback'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </FullScreen>
  );
}
